import { useState, useEffect, useRef } from 'react'
import { ChevronLeft, ChevronRight, Check, X, Clock, CalendarDays, Bell, BellOff, Car } from 'lucide-react'
import { useT } from '../i18n/useT'

// ── Helpers ───────────────────────────────────────
function toISO(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function parseISO(iso) {
  if (!iso) return null
  const [y, m, d] = iso.split('-').map(Number)
  return { year: y, month: m - 1, day: d }
}

function formatDisplay(iso, lang) {
  if (!iso) return null
  // Use T12:00:00 to avoid midnight UTC conversion artifacts
  return new Intl.DateTimeFormat(lang === 'it' ? 'it-IT' : 'en-GB', {
    weekday: 'short', day: 'numeric', month: 'long'
  }).format(new Date(iso + 'T12:00:00'))
}

// Preset orari rapidi — mattina / pomeriggio / sera / notte.
const TIME_PRESETS = ['09:00', '14:00', '18:00', '21:00']
const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'))
const MINS  = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55']

// Wheel iOS-style: 3 righe visibili × 36px = 108px (compatto per smartphone),
// riga centrale = selezione, una riga sopra e una sotto in grigio.
const WHEEL_ITEM_H = 36
const WHEEL_VISIBLE = 3
const WHEEL_PAD = WHEEL_ITEM_H * Math.floor(WHEEL_VISIBLE / 2)

// Trova il preset più vicino a "ora attuale" (in minuti dal mezzogiorno).
function findNearestPreset(date = new Date()) {
  const mins = date.getHours() * 60 + date.getMinutes()
  let best = TIME_PRESETS[0], bestDist = Infinity
  for (const p of TIME_PRESETS) {
    const [h, m] = p.split(':').map(Number)
    const d = Math.abs(mins - (h * 60 + m))
    if (d < bestDist) { best = p; bestDist = d }
  }
  return best
}

// Indice nella scala MINS (step 5) più vicino a un valore minuto qualsiasi.
function nearestMinIdx(mm) {
  const num = parseInt(mm, 10)
  if (isNaN(num)) return -1
  let best = 0, bestDist = Infinity
  MINS.forEach((m, i) => {
    const d = Math.abs(parseInt(m, 10) - num)
    if (d < bestDist) { best = i; bestDist = d }
  })
  return best
}

// ── Wheel column (iOS-style scroller con snap) ────────
function TimeWheel({ items, idx, onPickIdx, ariaLabel }) {
  const ref = useRef(null)
  const isProgrammatic = useRef(false)
  const isFirstMount = useRef(true)
  const scrollEndTimer = useRef(null)
  const [centerIdx, setCenterIdx] = useState(idx)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const target = idx * WHEEL_ITEM_H
    setCenterIdx(idx)
    if (Math.abs(el.scrollTop - target) > 1) {
      isProgrammatic.current = true
      if (isFirstMount.current) {
        el.scrollTop = target
        isFirstMount.current = false
        setTimeout(() => { isProgrammatic.current = false }, 60)
      } else {
        el.scrollTo({ top: target, behavior: 'smooth' })
        setTimeout(() => { isProgrammatic.current = false }, 420)
      }
    } else {
      isFirstMount.current = false
    }
  }, [idx])

  const onScroll = () => {
    const el = ref.current
    if (!el) return
    const newIdx = Math.round(el.scrollTop / WHEEL_ITEM_H)
    const clamped = Math.max(0, Math.min(items.length - 1, newIdx))
    setCenterIdx(clamped)
    if (isProgrammatic.current) return
    if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current)
    scrollEndTimer.current = setTimeout(() => {
      if (clamped !== idx) onPickIdx(clamped)
    }, 110)
  }

  return (
    <div className="dtp-wheel" ref={ref} onScroll={onScroll} aria-label={ariaLabel}>
      <div className="dtp-wheel-spacer" style={{ height: WHEEL_PAD }} />
      {items.map((item, i) => {
        const dist = Math.abs(i - centerIdx)
        return (
          <button
            type="button"
            key={item}
            className={`dtp-wheel-item${i === centerIdx ? ' is-active' : ''}`}
            data-dist={dist > 2 ? 'far' : dist}
            onClick={() => onPickIdx(i)}
          >
            {item}
          </button>
        )
      })}
      <div className="dtp-wheel-spacer" style={{ height: WHEEL_PAD }} />
    </div>
  )
}

// ── Main component ────────────────────────────────
const REMINDER_KEYS = [
  { value: '',     key: 'dtp_reminder_none' },
  { value: '0',    key: 'dtp_reminder_0' },
  { value: '5',    key: 'dtp_reminder_5m' },
  { value: '10',   key: 'dtp_reminder_10m' },
  { value: '15',   key: 'dtp_reminder_15m' },
  { value: '30',   key: 'dtp_reminder_30m' },
  { value: '60',   key: 'dtp_reminder_1h' },
  { value: '120',  key: 'dtp_reminder_2h' },
  { value: '1440', key: 'dtp_reminder_1d' },
]

// DURATION_OPTIONS rimosso dalla UI — duration_minutes resta nel DB,
// solo non è più selezionabile via picker.

export function DateTimePicker({ value, timeValue, onDateChange, onTimeChange, reminderValue, onReminderChange, durationValue, onDurationChange, lang = 'it', minISO, hideTime = false, locationReminder }) {
  const t = useT()
  const today = new Date(); today.setHours(0, 0, 0, 0)

  const [open, setOpen] = useState(false)
  const [nav, setNav]   = useState(() => {
    const p = parseISO(value)
    return p
      ? { year: p.year,            month: p.month }
      : { year: today.getFullYear(), month: today.getMonth() }
  })

  const parsed    = parseISO(value)
  const minParsed = parseISO(minISO)

  // Day names
  const dayNames = lang === 'it'
    ? ['L', 'M', 'M', 'G', 'V', 'S', 'D']
    : ['M', 'T', 'W', 'T', 'F', 'S', 'S']

  const monthLabel = new Intl.DateTimeFormat(lang === 'it' ? 'it-IT' : 'en-GB', {
    month: 'long', year: 'numeric'
  }).format(new Date(nav.year, nav.month, 1))

  // Calendar cells
  const firstDay    = new Date(nav.year, nav.month, 1).getDay()
  const daysInMonth = new Date(nav.year, nav.month + 1, 0).getDate()
  const offset      = (firstDay + 6) % 7
  const cells = []
  for (let i = 0; i < offset; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)
  while (cells.length % 7 !== 0) cells.push(null)

  const isDisabled = (d) => {
    if (!minParsed || !d) return false
    const cell = new Date(nav.year, nav.month, d)
    return cell < new Date(minParsed.year, minParsed.month, minParsed.day)
  }
  const isSelected = (d) => parsed && d &&
    parsed.year === nav.year && parsed.month === nav.month && parsed.day === d
  const isToday = (d) => d &&
    nav.year === today.getFullYear() && nav.month === today.getMonth() && d === today.getDate()

  const handleDay = (d) => {
    if (!d || isDisabled(d)) return
    onDateChange(toISO(nav.year, nav.month, d))
  }

  const prevMonth = () => setNav(n => n.month === 0
    ? { year: n.year - 1, month: 11 }
    : { ...n, month: n.month - 1 })
  const nextMonth = () => setNav(n => n.month === 11
    ? { year: n.year + 1, month: 0 }
    : { ...n, month: n.month + 1 })

  // Reminder expand
  const [reminderExpanded, setReminderExpanded] = useState(false)

  // Reminder dipende dall'ora: se l'ora viene tolta, auto-reset su "nessuno".
  useEffect(() => {
    if (!timeValue && reminderValue && onReminderChange) {
      onReminderChange('')
      setReminderExpanded(false)
    }
  }, [timeValue]) // eslint-disable-line react-hooks/exhaustive-deps

  // All'apertura del picker, se non c'è ancora una data → seleziona oggi
  // e imposta il preset orario più vicino all'ora corrente.
  useEffect(() => {
    if (!open) return
    if (!value) {
      const iso = toISO(today.getFullYear(), today.getMonth(), today.getDate())
      onDateChange(iso)
      if (!timeValue && !hideTime) {
        onTimeChange(findNearestPreset())
      }
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  // Sta nel passato? (per disabilitare la cella del giorno)
  const todayISO = toISO(today.getFullYear(), today.getMonth(), today.getDate())
  const isPast = (d) => {
    if (!d) return false
    const iso = toISO(nav.year, nav.month, d)
    return iso < todayISO
  }

  const displayLabel = value
    ? formatDisplay(value, lang)
    : t('dtp_select_date')

  return (
    <>
      {/* ── Trigger ── */}
      <div className="dtp-trigger-wrap">
        <button
          type="button"
          className={`dtp-trigger${value ? ' has-value' : ''}`}
          onClick={() => setOpen(true)}
        >
          <CalendarDays size={15} className="dtp-trigger-icon" />
          <span className="dtp-trigger-label">{displayLabel}</span>
          {value && timeValue && (
            <span className="dtp-trigger-time">
              <Clock size={12} /> {timeValue}
            </span>
          )}
          {onReminderChange && value && (
            <span className={`dtp-trigger-reminder${reminderValue ? '' : ' off'}`}>
              {reminderValue ? <Bell size={13} /> : <BellOff size={13} />}
            </span>
          )}
        </button>
        {value && (
          <button
            type="button"
            className="dtp-trigger-clear"
            onClick={() => { onDateChange(''); onTimeChange(''); if (onReminderChange) onReminderChange('') }}
            title={t('dtp_clear_date')}
          >
            <X size={14} />
          </button>
        )}
      </div>
      {reminderValue && value && timeValue && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="dtp-reminder-badge" onClick={() => setOpen(true)}>
            <Bell size={13} />
            <span>{t(REMINDER_KEYS.find(o => o.value === reminderValue)?.key || 'dtp_reminder_none')}</span>
          </div>
          {locationReminder?.lastTravelTimeSeconds && (
            <div className="dtp-reminder-badge" style={{ cursor: 'default' }}>
              <Car size={13} />
              <span>{Math.ceil(locationReminder.lastTravelTimeSeconds / 60)} minuti di percorrenza</span>
            </div>
          )}
        </div>
      )}

      {/* ── Bottom sheet (V4 layout) ── */}
      {open && (
        <div className="dtp-overlay" onClick={() => setOpen(false)}>
          <div className="dtp-sheet dtp-sheet--v4 animate-slideUp" onClick={e => e.stopPropagation()}>
            <div className="dtp-handle" />

            {/* Month nav: chevron circolari + mese centrato */}
            <div className="dtp-nav dtp-nav--v4">
              <button type="button" className="dtp-nav-btn dtp-nav-btn--v4" onClick={prevMonth} aria-label="Mese precedente">
                <ChevronLeft size={18} />
              </button>
              <span className="dtp-month-label">{monthLabel}</span>
              <button type="button" className="dtp-nav-btn dtp-nav-btn--v4" onClick={nextMonth} aria-label="Mese successivo">
                <ChevronRight size={18} />
              </button>
            </div>

            {/* Day names header */}
            <div className="dtp-day-names">
              {dayNames.map((n, i) => (
                <div
                  key={i}
                  className={`dtp-day-name${i >= 5 ? ' weekend' : ''}`}
                >{n}</div>
              ))}
            </div>

            {/* Grid — celle circolari V4 */}
            <div className="dtp-grid dtp-grid--v4">
              {cells.map((d, i) => {
                const col     = i % 7
                const weekend = d && col >= 5
                const past    = d && isPast(d) && !isSelected(d)
                const disabled = !d || isDisabled(d)
                return (
                  <button
                    type="button"
                    key={i}
                    className={[
                      'dtp-day',
                      !d              ? 'empty'    : '',
                      isSelected(d)   ? 'selected' : '',
                      isToday(d) && !isSelected(d) ? 'today' : '',
                      disabled        ? 'disabled' : '',
                      weekend && !isSelected(d) && !isToday(d) && !past ? 'weekend' : '',
                      past && !isToday(d) ? 'past' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => handleDay(d)}
                    disabled={disabled}
                  >
                    {d || ''}
                  </button>
                )
              })}
            </div>

            {/* Ora — 4 preset rapidi (09/14/18/21) + wheel iOS-style sempre
                visibile (ore/minuti, step 5min) con highlight centrale.
                Omessa quando hideTime=true (uso date-only per es. "Termina"). */}
            {!hideTime && (() => {
              const [hh, mm] = (timeValue || '').split(':')
              const fallbackHour = findNearestPreset().split(':')[0]
              const hourIdx = HOURS.indexOf(hh)
              const minIdx = nearestMinIdx(mm)
              const safeHourIdx = hourIdx >= 0 ? hourIdx : HOURS.indexOf(fallbackHour)
              const safeMinIdx = minIdx >= 0 ? minIdx : 0

              const handlePickHour = (i) => {
                const h = HOURS[i]
                const m = MINS[safeMinIdx >= 0 ? safeMinIdx : 0]
                onTimeChange(`${h}:${m}`)
              }
              const handlePickMin = (i) => {
                const h = hh && HOURS.indexOf(hh) >= 0 ? hh : fallbackHour
                onTimeChange(`${h}:${MINS[i]}`)
              }

              return (
                <div className="dtp-time-block">
                  <div className="dtp-time-block-presets">
                    {TIME_PRESETS.map(slot => {
                      const active = timeValue === slot
                      return (
                        <button
                          key={slot}
                          type="button"
                          className={`dtp-time-preset${active ? ' active' : ''}`}
                          onClick={() => onTimeChange(active ? '' : slot)}
                        >
                          {slot}
                        </button>
                      )
                    })}
                  </div>
                  <div className={`dtp-wheel-section${!timeValue ? ' is-empty' : ''}`}>
                    <div className="dtp-wheel-band" />
                    <div className="dtp-wheel-cols">
                      <TimeWheel
                        items={HOURS}
                        idx={safeHourIdx}
                        onPickIdx={handlePickHour}
                        ariaLabel={t('dtp_time') + ' (h)'}
                      />
                      <span className="dtp-wheel-colon">:</span>
                      <TimeWheel
                        items={MINS}
                        idx={safeMinIdx}
                        onPickIdx={handlePickMin}
                        ariaLabel={t('dtp_time') + ' (m)'}
                      />
                    </div>
                  </div>
                </div>
              )
            })()}
            {/* NB: campo "Durata" rimosso dalla UI (il DB mantiene duration_minutes). */}

            {/* Reminder — selezionabile SOLO se è già scelta un'ora.
                Nascosto se hideTime (non ha senso senza ora). */}
            {!hideTime && onReminderChange && (
              <div className="dtp-reminder-wrap">
                <button
                  type="button"
                  className={`dtp-inline-row dtp-inline-row--button${!timeValue ? ' disabled' : ''}`}
                  onClick={() => setReminderExpanded(v => !v)}
                  disabled={!timeValue}
                  aria-expanded={reminderExpanded}
                >
                  <Bell size={15} />
                  <span className="dtp-inline-label">{t('dtp_reminder')}</span>
                  <span className="dtp-inline-value">
                    {t(REMINDER_KEYS.find(o => o.value === (reminderValue || ''))?.key || 'dtp_reminder_none')}
                  </span>
                </button>
                {reminderExpanded && (
                  <div className="dtp-picklist dtp-picklist--up" role="listbox">
                    {REMINDER_KEYS.map(o => {
                      const active = (reminderValue || '') === o.value
                      return (
                        <button
                          key={o.value}
                          type="button"
                          role="option"
                          aria-selected={active}
                          className={`dtp-picklist-opt${active ? ' active' : ''}`}
                          onClick={() => { onReminderChange(o.value); setReminderExpanded(false) }}
                        >
                          <span className="dtp-picklist-label">{t(o.key)}</span>
                          {active && <Check size={16} className="dtp-picklist-check" />}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Bottom CTA: riepilogo (data · ora · promemoria) + Conferma */}
            <div className="dtp-cta-row">
              {value && (() => {
                const reminderKey = REMINDER_KEYS.find(o => o.value === (reminderValue || ''))?.key
                const reminderActive = !!reminderValue && reminderKey && reminderKey !== 'dtp_reminder_none'
                return (
                  <div className="dtp-cta-summary">
                    <div className="dtp-cta-summary-label">{t('dtp_summary_label')}</div>
                    <div className="dtp-cta-summary-value">
                      <span>{formatDisplay(value, lang)}</span>
                      {timeValue && <span> · {timeValue}</span>}
                      {reminderActive && (
                        <span> · {t(reminderKey)}</span>
                      )}
                    </div>
                  </div>
                )
              })()}
              <button
                type="button"
                className="btn btn-primary dtp-confirm"
                onClick={() => setOpen(false)}
              >
                {t('dtp_confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
